import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  Fade,
  Slide,
  Zoom,
  useScrollTrigger,
  AppBar,
  Toolbar,
  IconButton,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import PollIcon from '@mui/icons-material/Poll';
import GroupsIcon from '@mui/icons-material/Groups';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import PeopleIcon from '@mui/icons-material/People';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    setIsVisible(true);
    
    const observers = sectionRefs.current.map((ref, index) => {
      if (!ref) return null;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
            }
          });
        },
        { threshold: 0.1 }
      );
      
      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>OpinLoop - Share Your Opinions, Connect with Like-Minded People</title>
        <meta name="description" content="OpinLoop is a platform where you can share your opinions, get feedback from like-minded people, and engage in meaningful discussions. Join the conversation today!" />
        <meta name="keywords" content="opinions, discussion, social platform, feedback, community, debate, share thoughts, like-minded people" />
        <meta property="og:title" content="OpinLoop - Share Your Opinions, Connect with Like-Minded People" />
        <meta property="og:description" content="OpinLoop is a platform where you can share your opinions, get feedback from like-minded people, and engage in meaningful discussions." />
        <meta property="og:type" content="website" />
        <meta property="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://opinloop.com/" />
        
        {/* Structured Data (JSON-LD) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "OpinLoop",
            "url": "https://opinloop.com",
            "description": "OpinLoop is a platform where you can share your opinions, get feedback from like-minded people, and engage in meaningful discussions.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://opinloop.com/home?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      
    <Box sx={{ minHeight: '100vh', background: '#ffffff', overflow: 'hidden' }}>
      {/* Navigation Bar */}
      <AppBar
        position="fixed"
        sx={{
          background: 'rgba(102, 126, 234, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 1100
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src="/images/logo.png"
              alt="OpinLoop"
              onClick={() => navigate('/')}
              sx={{
                height: { xs: 32, sm: 40 },
                width: 'auto',
                cursor: 'pointer',
                objectFit: 'contain',
                borderRadius: 2,
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            />
            <Box
              component="a"
              href="https://releasepilot.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                opacity: 0.9,
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 1,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Box
                component="img"
                src="/images/rp_header_logo.png"
                alt="Releasepilot"
                sx={{
                  height: { xs: 20, sm: 24 },
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'brightness(0) invert(1)'
                }}
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => navigate('/search')}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                minHeight: { xs: 36, sm: 40 },
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Search
              </Box>
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                minHeight: { xs: 36, sm: 40 },
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              Login
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/signup')}
              sx={{
                background: 'white',
                color: '#667eea',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                minHeight: { xs: 36, sm: 40 },
                fontWeight: 600,
                '&:hover': {
                  background: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                },
                transition: 'all 0.2s ease'
              }}
            >
              Sign Up
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Spacer for fixed AppBar */}
      <Toolbar />

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
          backgroundSize: '200% 200%',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.08) 0%, transparent 50%)',
            animation: 'gradientShift 15s ease infinite'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
            animation: 'float 20s ease-in-out infinite'
          }
        }}
      >
        <style>
          {`
            @keyframes gradientShift {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            @keyframes float {
              0%, 100% { transform: translate(0, 0); }
              33% { transform: translate(30px, -30px); }
              66% { transform: translate(-20px, 20px); }
            }
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(40px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes fadeInScale {
              from {
                opacity: 0;
                transform: scale(0.9);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
            @keyframes shimmer {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
            @keyframes pulse {
              0%, 100% { 
                transform: scale(1);
                opacity: 1;
              }
              50% { 
                transform: scale(1.05);
                opacity: 0.9;
              }
            }
            .hero-title {
              background: linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.9) 50%, #ffffff 100%);
              background-size: 200% 200%;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
              animation: shimmer 3s ease-in-out infinite;
            }
            .animate-in {
              animation: fadeInUp 0.8s ease-out forwards;
            }
            .float-animation {
              animation: float 6s ease-in-out infinite;
            }
          `}
        </style>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'pulse 8s ease-in-out infinite',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '15%',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
            filter: 'blur(50px)',
            animation: 'pulse 10s ease-in-out infinite',
            zIndex: 0
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in={isVisible} timeout={1200}>
            <Box
              sx={{
                textAlign: 'center',
                color: 'white',
                py: { xs: 6, md: 10 },
                px: { xs: 2, md: 4 }
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  mb: { xs: 3, md: 4 },
                  animation: 'fadeInScale 0.8s ease-out',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Box
                  component="img"
                  src="/images/logo.png"
                  alt="OpinLoop"
                  sx={{
                    height: { xs: 80, sm: 100, md: 120, lg: 140 },
                    width: 'auto',
                    objectFit: 'contain',
                    borderRadius: 3,
                    filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
              </Box>

              {/* Main Title */}
              <Box
                sx={{
                  mb: 3,
                  animation: 'fadeInScale 1s ease-out 0.2s both'
                }}
              >
                <Typography
                  variant="h1"
                  component="h1"
                  className="hero-title"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5.5rem' },
                    mb: 2,
                    letterSpacing: { xs: '-0.02em', md: '-0.03em' },
                    lineHeight: 1.1,
                    textShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '80px',
                      height: '4px',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
                      borderRadius: '2px'
                    }
                  }}
                >
                  Where Opinions
                  <br />
                  <Box component="span" sx={{ 
                    background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Connect
                  </Box>
                </Typography>
              </Box>

              {/* Subtitle */}
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  maxWidth: 700,
                  mx: 'auto',
                  fontWeight: 300,
                  fontSize: { xs: '1.125rem', md: '1.375rem' },
                  lineHeight: 1.6,
                  animation: 'fadeInUp 1.2s ease-out 0.3s both',
                  textShadow: '0 2px 10px rgba(0,0,0,0.15)',
                  letterSpacing: '0.01em'
                }}
              >
                Share your thoughts, vote on opinions, and engage in meaningful discussions
                <br />
                <Box component="span" sx={{ opacity: 0.9, fontSize: '0.9em' }}>
                  in a clean, respectful environment
                </Box>
              </Typography>

              {/* CTA Buttons */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 2.5, 
                  justifyContent: 'center', 
                  flexWrap: 'wrap',
                  animation: 'fadeInUp 1.4s ease-out 0.6s both',
                  mt: 5
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/signup')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    color: '#667eea',
                    fontWeight: 600,
                    fontSize: '1rem',
                    borderRadius: 4,
                    textTransform: 'none',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15), 0 4px 8px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                      transition: 'left 0.5s ease'
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.2), 0 6px 12px rgba(0,0,0,0.15)',
                      '&::before': {
                        left: '100%'
                      }
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/search')}
                  startIcon={<SearchIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderWidth: 2,
                    borderColor: 'rgba(255,255,255,0.8)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    borderRadius: 4,
                    textTransform: 'none',
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.15)',
                      borderColor: 'rgba(255,255,255,1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Search Opinions
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderWidth: 2,
                    borderColor: 'rgba(255,255,255,0.8)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    borderRadius: 4,
                    textTransform: 'none',
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'rgba(255,255,255,0.1)',
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                      '&::before': {
                        transform: 'scaleX(1)'
                      }
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Sign In
                </Button>
              </Box>

              {/* Trust Indicators */}
              <Box
                sx={{
                  mt: 6,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 4,
                  flexWrap: 'wrap',
                  animation: 'fadeInUp 1.6s ease-out 0.9s both',
                  opacity: 0.85
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '2rem', mb: 0.5 }}>
                    10K+
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', opacity: 0.9 }}>
                    Active Users
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '2rem', mb: 0.5 }}>
                    50K+
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', opacity: 0.9 }}>
                    Opinions Shared
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '2rem', mb: 0.5 }}>
                    1M+
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', opacity: 0.9 }}>
                    Votes Cast
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Section 1: Share Your Perspective */}
      <Box
        ref={(el: HTMLDivElement | null) => { sectionRefs.current[0] = el; }}
        sx={{
          py: { xs: 4, md: 6 },
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 500,
                      mb: 1.5,
                      color: 'primary.main'
                    }}
                  >
                    Share Your Perspective
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      mb: 2,
                      lineHeight: 1.6
                    }}
                  >
                    Express your unique thoughts and viewpoints in a space designed for meaningful expression.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Card
                      sx={{
                        p: 1.5,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <EditIcon sx={{ fontSize: 24, color: 'primary.main', mb: 0.5 }} />
                      <Typography variant="body2" fontWeight={500}>
                        Quick & Easy
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Share in seconds
                      </Typography>
                    </Card>
                    <Card
                      sx={{
                        p: 1.5,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <TrendingUpIcon sx={{ fontSize: 24, color: 'primary.main', mb: 0.5 }} />
                      <Typography variant="body2" fontWeight={500}>
                        Reach Many
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Connect with thousands
                      </Typography>
                    </Card>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 300, md: 500 },
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)',
                  animation: 'float 6s ease-in-out infinite'
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80"
                  alt="Share Perspective"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 2: Collect Opinions / Votes / Suggestions */}
      <Box
        ref={(el: HTMLDivElement | null) => { sectionRefs.current[1] = el; }}
        sx={{
          py: { xs: 4, md: 6 },
          background: 'linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)',
          position: 'relative'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" direction="row-reverse">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 'bold',
                      mb: 3,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Collect Opinions & Votes
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'text.secondary',
                      mb: 2,
                      lineHeight: 1.8,
                      fontSize: '1.2rem'
                    }}
                  >
                    Gather valuable feedback, suggestions, and votes from your community. 
                    See what people really think and make data-driven decisions based on collective wisdom.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Card
                      sx={{
                        p: 1.5,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <PollIcon sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
                      <Typography variant="body2" fontWeight="bold">
                        Real-time Votes
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Instant feedback
                      </Typography>
                    </Card>
                    <Card
                      sx={{
                        p: 1.5,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <ThumbUpIcon sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
                      <Typography variant="body2" fontWeight="bold">
                        Clear Insights
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Visual analytics
                      </Typography>
                    </Card>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 300, md: 500 },
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)',
                  animation: 'float 6s ease-in-out infinite',
                  animationDelay: '1s'
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
                  alt="Collect Votes"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 3: Build a Like-Minded Community */}
      <Box
        ref={(el: HTMLDivElement | null) => { sectionRefs.current[2] = el; }}
        sx={{
          py: { xs: 4, md: 6 },
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%)',
          position: 'relative'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 'bold',
                      mb: 3,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Build a Like-Minded Community
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'text.secondary',
                      mb: 2,
                      lineHeight: 1.8,
                      fontSize: '1.2rem'
                    }}
                  >
                    Connect with people who share your interests and values. 
                    Build meaningful relationships through respectful discussions and discover perspectives 
                    that challenge and inspire you.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Card
                      sx={{
                        p: 1.5,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <GroupsIcon sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
                      <Typography variant="body2" fontWeight="bold">
                        Connect
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Find your tribe
                      </Typography>
                    </Card>
                    <Card
                      sx={{
                        p: 1.5,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <CommentIcon sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
                      <Typography variant="body2" fontWeight="bold">
                        Discuss
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Meaningful talks
                      </Typography>
                    </Card>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 300, md: 500 },
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)',
                  animation: 'float 6s ease-in-out infinite',
                  animationDelay: '2s'
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80"
                  alt="Build Community"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Grid Section */}
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Why Choose OpinLoop?
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                icon: <EditIcon sx={{ fontSize: 50 }} />,
                title: 'Express Freely',
                description: 'Share your thoughts without limits in a supportive environment'
              },
              {
                icon: <PollIcon sx={{ fontSize: 50 }} />,
                title: 'Get Feedback',
                description: 'Collect votes and opinions from a diverse community'
              },
              {
                icon: <GroupsIcon sx={{ fontSize: 50 }} />,
                title: 'Build Connections',
                description: 'Find and connect with like-minded individuals'
              },
              {
                icon: <CommentIcon sx={{ fontSize: 50 }} />,
                title: 'Engage Meaningfully',
                description: 'Participate in respectful, threaded discussions'
              }
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Zoom in={true} timeout={800 + index * 200}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 4,
                      background: 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 4,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-12px) scale(1.02)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    <CardContent>
                      <Avatar
                        sx={{
                          width: 100,
                          height: 100,
                          mx: 'auto',
                          mb: 3,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)'
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                      <Typography variant="h6" component="h3" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%)',
          position: 'relative'
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={12}
            sx={{
              p: { xs: 4, md: 8 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              backdropFilter: 'blur(10px)',
              borderRadius: 6,
              border: '1px solid rgba(102, 126, 234, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                background: 'radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
                borderRadius: '50%'
              }
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              fontWeight="bold"
              sx={{
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Ready to Share Your Opinion?
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                mb: 5,
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.2rem' }
              }}
            >
              Join thousands of users sharing their thoughts and engaging in meaningful discussions. 
              Your perspective matters.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #653a91 100%)',
                  transform: 'translateY(-4px) scale(1.05)',
                  boxShadow: '0 15px 40px rgba(102, 126, 234, 0.5)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Create Account
              <ArrowForwardIcon sx={{ ml: 1 }} />
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Footer with Releasepilot Branding */}
      <Box
        sx={{
          py: { xs: 4, md: 5 },
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Link
              href="https://releasepilot.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                color: 'white',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  opacity: 0.9
                }
              }}
            >
              <Box
                component="img"
                src="/images/rp_header_logo.png"
                alt="Releasepilot"
                sx={{
                  height: { xs: 24, sm: 28 },
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'brightness(0) invert(1)'
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  fontWeight: 500,
                  opacity: 0.95
                }}
              >
                Part of Releasepilot
              </Typography>
            </Link>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255,255,255,0.8)',
                fontSize: { xs: '0.75rem', sm: '0.8125rem' }
              }}
            >
              © {new Date().getFullYear()} OpinLoop. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
      </Box>
    </>
  );
};

export default HomePage;
